import React, { useEffect, useMemo, useState } from 'react';
import { Empty, Input } from 'antd';
import { useSelector } from 'react-redux';
import { Link, useLocation, useOutletContext } from 'react-router-dom';
import * as Icons from '@ant-design/icons';
import { Module } from '../../../types/Module';
import { customThemes } from '../../../themeConfig';

/* ===================== ICON HELPER ===================== */
const getDynamicIcon = (name?: string) => {
    if (!name) return <Icons.AppstoreOutlined />;
    const IconComponent =
        (Icons as any)[name] ||
        (Icons as any)[`${name}Outlined`] ||
        (Icons as any)[`${name}Filled`] ||
        (Icons as any)[`${name}TwoTone`];
    return IconComponent ? <IconComponent /> : <Icons.AppstoreOutlined />;
};

/* ===================== WOODWORK & ERP COLOR SCHEME ===================== */
const iconStyles = [
    { bg: 'rgba(212, 175, 55, 0.08)', color: '#D4AF37', glow: 'rgba(212, 175, 55, 0.18)' },  // Gold / Brass
    { bg: 'rgba(160, 82, 45, 0.08)', color: '#A0522D', glow: 'rgba(160, 82, 45, 0.18)' },   // Sienna / Wood Brown
    { bg: 'rgba(30, 136, 229, 0.08)', color: '#1E88E5', glow: 'rgba(30, 136, 229, 0.18)' },  // Tech Blue
    { bg: 'rgba(67, 160, 71, 0.08)', color: '#43A047', glow: 'rgba(67, 160, 71, 0.18)' },   // Emerald Green
    { bg: 'rgba(230, 81, 0, 0.08)', color: '#E65100', glow: 'rgba(230, 81, 0, 0.18)' },    // Orange / Clay
    { bg: 'rgba(0, 172, 193, 0.08)', color: '#00ACC1', glow: 'rgba(0, 172, 193, 0.18)' },   // Cyan / Slate
    { bg: 'rgba(109, 76, 65, 0.08)', color: '#6D4C41', glow: 'rgba(109, 76, 65, 0.18)' },   // Dark Brown
    { bg: 'rgba(94, 53, 177, 0.08)', color: '#5E35B1', glow: 'rgba(94, 53, 177, 0.18)' },   // Deep Royal
];

const MenuPage: React.FC = () => {
    const { pathname } = useLocation();
    const [searchQuery, setSearchQuery] = useState('');

    // Load context from AppLayout
    const context = useOutletContext<any>() || {};
    const darkMode = context.darkMode || false;
    const baseTheme = darkMode ? customThemes.dark : customThemes.light;
    const themeStyles = { ...baseTheme, ...context.themeStyles };

    // ===================== HIGH END ELEVATED COLOR SCHEME FOR ERP VISUAL DEPTH =====================
    const customLayoutBg = darkMode ? '#0f172a' : '#f8fafc'; // Slate-900 vs Slate-50
    const customCardBg = darkMode ? '#1e293b' : '#ffffff';   // Slate-800 vs Pure White
    const customCardBorder = darkMode ? 'rgba(51, 65, 85, 0.45)' : 'rgba(226, 232, 240, 0.8)'; // Slate-700 vs Slate-200
    const customTextMain = darkMode ? '#f8fafc' : '#0f172a';  // Slate-50 vs Slate-900
    const customTextSub = darkMode ? '#94a3b8' : '#64748b';   // Slate-400 vs Slate-500

    // Load all flat modules from Redux
    const rawModules = useSelector(
        (state: any) => state.user?.modules as Module[],
    );

    // Normalize current URL path to match database URLs
    const currentUrl = useMemo(() => {
        return pathname.replace(/^\//, "").replace(/\/$/, "").toLowerCase();
    }, [pathname]);

    // 1. Find the parent module matching this pathname
    const parentModule = useMemo(() => {
        if (!rawModules || rawModules.length === 0) return null;

        let match = rawModules.find(
            (m) => m.module_url && m.module_url.replace(/^\//, "").replace(/\/$/, "").toLowerCase() === currentUrl
        );

        if (!match) {
            match = rawModules.find(
                (m) =>
                    m.module_name &&
                    m.module_name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "") === currentUrl
            );
        }

        return match;
    }, [rawModules, currentUrl]);

    // 2. Fetch all child modules belonging to this parent module
    const childModules = useMemo(() => {
        if (!parentModule || !rawModules) return [];
        return rawModules.filter(
            (m) => m.module_parent === parentModule.module_id
        );
    }, [parentModule, rawModules]);

    // Build the recursive subtree under this parent module
    const buildSubtree = (parentId: number): any[] => {
        const directChildren = rawModules.filter(
            m => m.module_parent === parentId && 
                 m.module_status !== 0 && 
                 m.module_name && 
                 m.module_name.trim() !== ''
        );
        directChildren.sort((a, b) => {
            return (Number(a.module_order) || 0) - (Number(b.module_order) || 0) || 
                   (Number(a.module_id) || 0) - (Number(b.module_id) || 0);
        });

        return directChildren.map(module => {
            const subChildren = buildSubtree(module.module_id);
            return {
                ...module,
                children: subChildren
            };
        });
    };

    // Filter tree recursively based on query
    const filterTree = (nodes: any[], query: string): any[] => {
        if (!query) return nodes;
        const lowerQuery = query.toLowerCase();

        return nodes
            .map(node => {
                const matchesSelf = node.module_name.toLowerCase().includes(lowerQuery);
                const filteredChildren = matchesSelf
                    ? node.children
                    : filterTree(node.children || [], query);

                const matchesChild = filteredChildren.length > 0;

                if (matchesSelf || matchesChild) {
                    return {
                        ...node,
                        children: filteredChildren
                    };
                }
                return null;
            })
            .filter((node): node is any => node !== null);
    };

    const fullTree = useMemo(() => {
        if (!parentModule || !rawModules) return [];
        return buildSubtree(parentModule.module_id);
    }, [parentModule, rawModules]);

    const filteredTree = useMemo(() => {
        return filterTree(fullTree, searchQuery);
    }, [fullTree, searchQuery]);

    /* ================= INJECT DYNAMIC ERP HOVER STYLES ================= */
    useEffect(() => {
        const styleId = "menubar-modern-cards-style";
        let styleTag = document.getElementById(styleId) as HTMLStyleElement | null;
        if (!styleTag) {
            styleTag = document.createElement("style");
            styleTag.id = styleId;
            document.head.appendChild(styleTag);
        }
        styleTag.innerHTML = `
      @keyframes cardSlideIn {
        from {
          opacity: 0;
          transform: translateY(24px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      .erp-console-card {
        background: ${customCardBg} !important;
        border: 1px solid ${customCardBorder} !important;
        border-radius: 12px !important;
        padding: 20px !important;
        display: flex !important;
        flex-direction: column !important;
        justify-content: space-between !important;
        height: 140px !important;
        position: relative !important;
        overflow: hidden !important;
        transition: transform 0.8s cubic-bezier(0.25, 1, 0.5, 1), box-shadow 0.8s cubic-bezier(0.25, 1, 0.5, 1), border-color 0.8s cubic-bezier(0.25, 1, 0.5, 1), background-color 0.8s cubic-bezier(0.25, 1, 0.5, 1) !important;
        will-change: transform, box-shadow, border-color;
        cursor: pointer !important;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.01), 0 1px 2px rgba(0, 0, 0, 0.005) !important;
        animation: cardSlideIn 0.9s cubic-bezier(0.25, 1, 0.5, 1) both;
      }
      .erp-console-card::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        width: 4px;
        background-color: var(--accent-color, #1890ff);
        transition: width 0.7s cubic-bezier(0.25, 1, 0.5, 1);
        z-index: 2;
      }
      /* Highly-delayed glass sheen sweep reflection */
      .erp-console-card::after {
        content: '';
        position: absolute;
        top: -50%;
        left: -50%;
        width: 200%;
        height: 200%;
        background: linear-gradient(
          45deg,
          transparent 45%,
          ${darkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(255, 255, 255, 0.35)'} 50%,
          transparent 55%
        );
        transform: rotate(-45deg) translate(-75%, -75%);
        transition: transform 1.4s cubic-bezier(0.25, 1, 0.5, 1);
        pointer-events: none;
        z-index: 1;
      }
      .erp-console-card:hover {
        transform: translateY(-6px) !important;
        border-color: var(--accent-color) !important;
        box-shadow: 0 22px 30px -8px ${darkMode ? 'rgba(0, 0, 0, 0.5)' : 'rgba(148, 163, 184, 0.15)'}, 0 8px 12px -6px ${darkMode ? 'rgba(0, 0, 0, 0.5)' : 'rgba(148, 163, 184, 0.15)'}, 0 0 18px var(--accent-glow) !important;
      }
      .erp-console-card:hover::before {
        width: 6px;
      }
      .erp-console-card:hover::after {
        transform: rotate(-45deg) translate(75%, 75%);
      }
      .erp-icon-badge {
        width: 48px;
        height: 48px;
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 22px;
        transition: all 0.8s cubic-bezier(0.25, 1, 0.5, 1) !important;
        z-index: 2;
      }
      .erp-console-card:hover .erp-icon-badge {
        transform: scale(1.12) translateY(-2px) rotate(4deg) !important;
        box-shadow: 0 4px 12px var(--accent-glow) !important;
      }
      .erp-console-card:hover .erp-menu-card-accent-bar {
        width: 60px !important;
      }
      .erp-console-card:hover .erp-action-btn {
        background: var(--accent-color) !important;
        border-color: var(--accent-color) !important;
        color: #ffffff !important;
        transform: translateY(-1px) translateX(2px) !important;
        box-shadow: 0 4px 10px var(--accent-glow) !important;
      }
      .erp-action-btn {
        height: 32px !important;
        border-radius: 6px !important;
        font-size: 12px !important;
        font-weight: 600 !important;
        display: inline-flex !important;
        align-items: center !important;
        gap: 6px !important;
        padding: 0 14px !important;
        background: transparent !important;
        border: 1px solid ${darkMode ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)'} !important;
        color: ${customTextMain} !important;
        transition: all 0.7s cubic-bezier(0.25, 1, 0.5, 1) !important;
        z-index: 2;
      }
      .erp-search-field {
        transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1) !important;
      }
      .erp-search-field:focus, .erp-search-field:hover {
        border-color: ${themeStyles.colorPrimary} !important;
        box-shadow: 0 0 0 2px ${darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(7, 36, 60, 0.05)'} !important;
      }
    `;
    }, [customCardBg, customCardBorder, customTextMain, customTextSub, themeStyles, darkMode]);

    // Normalize absolute/relative target paths
    const getNormalizeLink = (url?: string) => {
        if (!url) return '#';
        if (url.startsWith('/') || url.startsWith('http')) return url;
        return `/${url}`;
    };

    return (
        <div style={{ padding: '32px 24px', minHeight: '90vh', backgroundColor: customLayoutBg, transition: 'background-color 0.3s ease' }}>

            {/* ERP CONTROL CENTRE HEADER BANNER */}
            {parentModule && (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '24px',
                    marginBottom: '36px',
                    borderBottom: `2px solid ${darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'}`,
                    paddingBottom: '28px'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '20px' }}>
                        <div>


                            <h1 style={{
                                fontSize: '32px',
                                fontWeight: '800',
                                margin: '0 0 6px 0',
                                letterSpacing: '-0.02em',
                                color: customTextMain,
                                textTransform: 'capitalize'
                            }}>
                                {parentModule.module_name} Directory
                            </h1>

                            <p style={{
                                fontSize: '15px',
                                margin: 0,
                                color: customTextSub
                            }}>
                                Access and manage the operational sections and system controls under this category.
                            </p>
                        </div>

                        {/* Search Console Input */}
                        {childModules.length > 0 && (
                            <Input
                                className="erp-search-field"
                                placeholder="Filter system submodules..."
                                prefix={<Icons.SearchOutlined style={{ color: customTextSub }} />}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                allowClear
                                style={{
                                    width: '300px',
                                    height: '40px',
                                    borderRadius: '6px',
                                    border: `1px solid ${darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.15)'}`,
                                    background: customCardBg,
                                    color: customTextMain,
                                    fontSize: '13px'
                                }}
                            />
                        )}
                    </div>
                </div>
            )}

            {/* BENTO CARDS GRID / GROUPS */}
            {filteredTree.length > 0 ? (
                <div>
                    {(() => {
                        let cardCounter = 0;

                        const renderCard = (child: any, idx: number) => {
                            const stylePreset = iconStyles[idx % iconStyles.length];
                            return (
                                <Link
                                    key={child.module_id}
                                    to={getNormalizeLink(child.module_url)}
                                    style={{ textDecoration: 'none' }}
                                >
                                    <div
                                        className="erp-console-card"
                                        style={{
                                            '--accent-color': stylePreset.color,
                                            '--accent-glow': stylePreset.glow,
                                            animationDelay: `${idx * 40}ms`
                                        } as React.CSSProperties}
                                    >
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                            <div
                                                className="erp-icon-badge"
                                                style={{
                                                    backgroundColor: stylePreset.bg,
                                                    color: stylePreset.color
                                                }}
                                            >
                                                {getDynamicIcon(child.module_icon || child.module_name)}
                                            </div>
                                            <div
                                                className="erp-menu-card-accent-bar"
                                                style={{
                                                    height: '3px',
                                                    width: '24px',
                                                    borderRadius: '2px',
                                                    backgroundColor: stylePreset.color,
                                                    transition: 'width 0.8s cubic-bezier(0.25, 1, 0.5, 1)'
                                                }}
                                            />
                                        </div>

                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <h3
                                                style={{
                                                    fontSize: '16px',
                                                    fontWeight: '700',
                                                    margin: 0,
                                                    color: customTextMain,
                                                    letterSpacing: '-0.01em',
                                                    textOverflow: 'ellipsis',
                                                    overflow: 'hidden',
                                                    whiteSpace: 'nowrap',
                                                    maxWidth: '70%'
                                                }}
                                            >
                                                {child.module_name}
                                            </h3>
                                            <div className="erp-action-btn">
                                                Open <Icons.RightOutlined style={{ fontSize: '9px' }} />
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            );
                        };

                        const renderNodesInOrder = (nodes: any[]): React.ReactNode => {
                            const chunks: { type: 'grid' | 'group'; items: any[] }[] = [];
                            nodes.forEach((node) => {
                                const isGroup = node.children && node.children.length > 0;
                                if (isGroup) {
                                    chunks.push({ type: 'group', items: [node] });
                                } else {
                                    const last = chunks[chunks.length - 1];
                                    if (last && last.type === 'grid') {
                                        last.items.push(node);
                                    } else {
                                        chunks.push({ type: 'grid', items: [node] });
                                    }
                                }
                            });

                            return (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                                    {chunks.map((chunk, cIdx) => {
                                        if (chunk.type === 'grid') {
                                            return (
                                                <div
                                                    key={`grid-${cIdx}`}
                                                    style={{
                                                        display: 'grid',
                                                        gridTemplateColumns: 'repeat(auto-fill, minmax(310px, 1fr))',
                                                        gap: '20px',
                                                        width: '100%',
                                                    }}
                                                >
                                                    {chunk.items.map((item) => {
                                                        const rendered = renderCard(item, cardCounter);
                                                        cardCounter++;
                                                        return rendered;
                                                    })}
                                                </div>
                                            );
                                        }

                                        const groupNode = chunk.items[0];
                                        return (
                                            <div
                                                key={groupNode.module_id}
                                                style={{
                                                    padding: '24px',
                                                    background: darkMode ? 'rgba(30, 41, 59, 0.4)' : 'rgba(255, 255, 255, 0.65)',
                                                    border: `1px dashed ${darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
                                                    borderRadius: '16px'
                                                }}
                                            >
                                                {/* Group Heading */}
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '10px',
                                                    marginBottom: '20px'
                                                }}>
                                                    <span style={{ fontSize: '20px', color: themeStyles.colorPrimary, display: 'flex', alignItems: 'center' }}>
                                                        {getDynamicIcon(groupNode.module_icon || groupNode.module_name)}
                                                    </span>
                                                    <h2 style={{
                                                        fontSize: '18px',
                                                        fontWeight: '700',
                                                        margin: 0,
                                                        color: customTextMain,
                                                        letterSpacing: '-0.01em'
                                                    }}>
                                                        {groupNode.module_name}
                                                    </h2>
                                                </div>

                                                {/* Sub-items rendered recursively preserving exact same order */}
                                                {renderNodesInOrder(groupNode.children || [])}
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        };

                        return renderNodesInOrder(filteredTree);
                    })()}
                </div>
            ) : (
                /* EMPTY / NO MATCH STATE */
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '35vh',
                        width: '100%'
                    }}
                >
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={
                            <span style={{ color: customTextSub }}>
                                {childModules.length > 0
                                    ? "No operational submodules match your search query."
                                    : "No operational modules configured under this category."}
                            </span>
                        }
                    />
                </div>
            )}
        </div>
    );
};

export default MenuPage;
